import { useMutateStopRecordingFromPause } from '@/hooks/mutation/useMutateStopSessionFromPause';
import { trackManualCaptureStop } from '@/lib/desktopExitCaptureStop';
import {
  openRecordingPauseAttempt,
  openRecordingStopAttempt,
} from '@/lib/telemetry/recordingControlOpeners';
import {
  appendRecordingControlCheckpoint,
  type RecordingControlOptions,
  type RecordingControlSurface,
  type RecordingControlTracking,
} from '@/lib/telemetry/recordingControlTracking';
import { logger } from '@/utils/logger/logger';
import {
  type RecordingCommandPolicy,
  type RecordingStopRoute,
  type RecordingTransport,
  useRecordingCommandPolicy,
} from '@heidi/capture/recording-commands';
import { RecordingStatus } from '@heidi/schema/ml-scribe';
import { useMutateUpdateSessionRecordingStatus } from '@heidi/session/data';
import { useCallback, useMemo } from 'react';

/** The part of `useRecording()` a pause or stop command reaches. */
export type RecordingControlRecorder = {
  transport: RecordingTransport;
  pauseRecording: (options?: RecordingControlOptions) => Promise<void> | void;
  stopRecording: (options?: RecordingControlOptions) => void;
};

export type RecordingControlCommand = {
  controlSurface: RecordingControlSurface;
  /** Surface effects that need the command accepted; runs before the attempt opens. */
  onAccepted?: () => void;
  /** Surface effects that run with the status write, just before the recorder call. */
  beforeRecorder?: () => void;
};

export type RecordingStopCommand = Omit<RecordingControlCommand, 'beforeRecorder'> & {
  fromPaused: boolean;
  /** As `beforeRecorder` on a pause, with the recorder call the stop runs. */
  beforeRecorder?: (decision: { route: RecordingStopRoute }) => void;
  /** A stop that only tears down capture, such as the demo flow, writes no status. */
  writeStatus?: boolean;
  onStopFromPauseError?: () => void;
};

/** `settled` follows the status write and recorder work for an accepted command. */
export type RecordingControlResult = {
  accepted: boolean;
  settled: Promise<void>;
};

export type RecordingControlCommands = {
  pause: (command: RecordingControlCommand) => RecordingControlResult;
  stop: (command: RecordingStopCommand) => RecordingControlResult;
};

const REJECTED: RecordingControlResult = { accepted: false, settled: Promise.resolve() };

/** One enum-only line per rejected press, so "pressed Pause, nothing happened" is visible. */
export const logRecordingCommandRejected = (
  command: 'pause' | 'stop',
  transport: RecordingTransport,
  controlSurface: RecordingControlSurface = 'mini_mode'
) => {
  // info, not debug: prod ships info and above, and only V1 repeats land here.
  logger.info({
    event_type: 'RECORDING_COMMAND_REJECTED',
    command,
    controlSurface,
    transport,
  });
};

/**
 * The one place a pause or stop press on `sessionId` is accepted and opens its
 * attempt; its status is applied to the cache, the recorder runs, then the
 * write is sent. A rejected press opens and writes nothing.
 */
export const useRecordingControlCommands = ({
  sessionId,
  recorder,
  policy: sharedPolicy,
}: {
  /** Every command targets this session: its status, its attempt and its stop from pause. */
  sessionId: string | undefined;
  recorder: RecordingControlRecorder;
  /** A surface with its own path passes its policy, so both share one window. */
  policy?: RecordingCommandPolicy;
}): RecordingControlCommands => {
  const { mutateAsync: updateRecordingStatus } = useMutateUpdateSessionRecordingStatus();
  const { mutate: stopFromPause } = useMutateStopRecordingFromPause({
    sessionId: sessionId ?? '',
  });
  const { transport, pauseRecording, stopRecording } = recorder;
  const ownPolicy = useRecordingCommandPolicy(transport);
  const policy = sharedPolicy ?? ownPolicy;

  const writeStatus = useCallback(
    (
      recordingStatus: RecordingStatus,
      command: Pick<RecordingControlCommand, 'controlSurface'>,
      tracking: RecordingControlTracking | undefined,
      onMutate: () => void
    ): Promise<void> => {
      if (!sessionId) {
        // No session to write: still reach the recorder so capture tears down.
        logger.warn({
          event_type: 'RECORDING_CONTROL_WITHOUT_SESSION',
          msg: '[recording-control] press with no session; recorder runs, no status written',
          command: recordingStatus === RecordingStatus.PAUSED ? 'pause' : 'stop',
          controlSurface: command.controlSurface,
        });
        onMutate();
        return Promise.resolve();
      }
      appendRecordingControlCheckpoint(tracking, 'mutation_dispatched');
      const settled = updateRecordingStatus({
        recordingStatus,
        sessionId,
        onMutate,
      }).then(() => undefined);
      // Fire-and-forget callers ignore it; the mutation logs the failure.
      settled.catch(() => undefined);
      return settled;
    },
    [sessionId, updateRecordingStatus]
  );

  const pause = useCallback(
    (command: RecordingControlCommand): RecordingControlResult => {
      if (!policy.acceptPause()) {
        logRecordingCommandRejected('pause', transport, command.controlSurface);
        return REJECTED;
      }
      command.onAccepted?.();
      const tracking = sessionId
        ? openRecordingPauseAttempt(sessionId, command.controlSurface)
        : undefined;
      const releaseManualStop = sessionId ? trackManualCaptureStop(sessionId) : undefined;
      let resolveRecorderStop: () => void = () => {};
      let recorderStopError: unknown;
      let recorderStopFailed = false;
      let recorderStarted = false;
      const recorderStopSettled = new Promise<void>((resolve) => {
        resolveRecorderStop = resolve;
      });
      const finishRecorderStop = (error?: unknown): void => {
        if (error !== undefined) {
          recorderStopFailed = true;
          recorderStopError = error;
        }
        resolveRecorderStop();
        releaseManualStop?.();
      };
      const statusSettled = writeStatus(RecordingStatus.PAUSED, command, tracking, () => {
        try {
          command.beforeRecorder?.();
          recorderStarted = true;
          void Promise.resolve(pauseRecording({ tracking })).then(
            () => finishRecorderStop(),
            (error: unknown) => finishRecorderStop(error)
          );
        } catch (error) {
          finishRecorderStop(error);
          throw error;
        }
      });
      const settled = statusSettled
        .then(
          () => recorderStopSettled,
          async (error: unknown) => {
            if (!recorderStarted) finishRecorderStop();
            await recorderStopSettled;
            throw error;
          }
        )
        .then(() => {
          if (recorderStopFailed) throw recorderStopError;
        });
      return { accepted: true, settled };
    },
    [policy, transport, sessionId, writeStatus, pauseRecording]
  );

  const stop = useCallback(
    (command: RecordingStopCommand): RecordingControlResult => {
      const decision = policy.acceptStop(command.fromPaused);
      if (!decision.accepted) {
        logRecordingCommandRejected('stop', transport, command.controlSurface);
        return REJECTED;
      }
      command.onAccepted?.();
      const tracking = sessionId
        ? openRecordingStopAttempt(sessionId, command.controlSurface, command.fromPaused)
        : undefined;

      const runRecorder = () => {
        command.beforeRecorder?.({ route: decision.route });
        if (decision.route === 'stopFromPause') {
          stopFromPause({ tracking }, { onError: command.onStopFromPauseError });
        } else {
          stopRecording({ tracking });
        }
      };
      if (command.writeStatus === false) {
        runRecorder();
        return { accepted: true, settled: Promise.resolve() };
      }
      const settled = writeStatus(RecordingStatus.IDLE, command, tracking, runRecorder);
      return { accepted: true, settled };
    },
    [policy, transport, sessionId, writeStatus, stopFromPause, stopRecording]
  );

  return useMemo(() => ({ pause, stop }), [pause, stop]);
};
